import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useGeocoding } from './useGeocoding';
import { AdherentMapLoadingBar } from './AdherentMapLoadingBar';
import { AdherentMapPopup } from './AdherentMapPopup';
import { AdherentMapProps, GeocodedAddress, convertToAddresses } from './AdherentMapTypes';

import 'leaflet/dist/leaflet.css';

// Fix for Leaflet marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = () => {
  return L.divIcon({
    html: `
      <div class="bg-primary-600 rounded-lg shadow-md text-white p-1 flex flex-col items-center justify-center min-w-[34px]">
        <div class="text-xs font-bold">1</div>
        <div class="text-[8px] whitespace-nowrap">adh.</div>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [40, 30],
    iconAnchor: [20, 15],
  });
};

// Map loading overlay component
const MapLoadingOverlay: React.FC<{ isLoading: boolean; progress: number }> = ({ isLoading, progress }) => {
  if (!isLoading) return null;
  
  return (
    <div className="absolute inset-0 bg-white bg-opacity-70 z-[1000] flex flex-col items-center justify-center">
      <div className="bg-white p-4 rounded-lg shadow-lg text-center max-w-md">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700 mx-auto mb-4"></div>
        <p className="text-lg font-medium text-gray-800 mb-2">Géocodage des adresses en cours...</p>
        <p className="text-sm text-gray-600 mb-3">{progress}% terminé</p>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Component to handle map updates
const MapUpdater: React.FC<{ addresses: GeocodedAddress[] }> = ({ addresses }) => {
  const map = useMap();

  useEffect(() => {
    if (addresses.length > 0) {
      const bounds = L.latLngBounds(
        addresses.map(addr => [addr.latitude, addr.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [addresses, map]);

  return null;
};

// MarkerClusterGroup component
const AdherentMarkerCluster: React.FC<{ addresses: GeocodedAddress[] }> = ({ addresses }) => {
  // Create a custom icon creator for clusters
  const createClusterIcon = (cluster: any) => {
    const childMarkers = cluster.getAllChildMarkers();
    const count = childMarkers.length;
    
    return L.divIcon({
      html: `
        <div class="bg-primary-600 text-white rounded-lg shadow-lg border-2 border-primary-300 p-2 flex flex-col items-center justify-center min-w-[45px]">
          <div class="text-sm font-bold">${count}</div>
          <div class="text-[8px] font-medium whitespace-nowrap">${count === 1 ? 'adhérent' : 'adhérents'}</div>
        </div>
      `,
      className: 'custom-cluster',
      iconSize: [60, 40],
      iconAnchor: [30, 20],
    });
  };

  return (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={60}
      spiderfyOnMaxZoom
      showCoverageOnHover
      zoomToBoundsOnClick
      iconCreateFunction={createClusterIcon}
    >
      {addresses.map((address) => (
        <Marker
          key={address.id}
          position={[address.latitude, address.longitude]}
          icon={createCustomIcon()}
        >
          <Popup maxWidth={400}>
            <AdherentMapPopup address={address} />
          </Popup>
        </Marker>
      ))}
    </MarkerClusterGroup>
  );
};

export const AdherentMapComponent: React.FC<AdherentMapProps> = ({ adherents, isLoading: dataLoading, className = '' }) => {
  // Convert adherent data to addresses for geocoding
  const addresses = useMemo(() => {
    return convertToAddresses(adherents);
  }, [adherents]);
  
  // Use the geocoding hook
  const { geocodedAddresses, loadingState } = useGeocoding(addresses);
  
  // Calculate loading progress percentage
  const loadingProgress = useMemo(() => {
    if (loadingState.total === 0) return 0;
    return Math.round((loadingState.completed / loadingState.total) * 100);
  }, [loadingState.completed, loadingState.total]);
  
  // Determine if we're still loading
  const isLoading = useMemo(() => 
    dataLoading || (loadingState.total > 0 && !loadingState.isComplete), 
    [dataLoading, loadingState.total, loadingState.isComplete]
  );

  // Determine if we should show the loading bar (hide if data is from cache)
  const showLoadingBar = useMemo(() => {
    // Only show loading bar if we actually have items to process and they're not done yet
    return loadingState.total > 0 && !loadingState.isComplete;
  }, [loadingState.total, loadingState.isComplete]);
  
  // Memoize the marker cluster component to prevent unnecessary rendering
  const markerCluster = useMemo(() => {
    if (geocodedAddresses.length === 0) return null;
    return <AdherentMarkerCluster addresses={geocodedAddresses} />;
  }, [geocodedAddresses]);

  return (
    <div className={className}>
      {showLoadingBar && <AdherentMapLoadingBar loadingState={loadingState} />}
      
      <div className="h-[500px] w-full rounded-lg overflow-hidden relative">
        <MapLoadingOverlay 
          isLoading={isLoading} 
          progress={loadingProgress} 
        />
        
        <MapContainer
          center={[46.603354, 1.888334]} // Center of France
          zoom={6}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapUpdater addresses={geocodedAddresses} />
          
          {markerCluster}
        </MapContainer>
      </div>
    </div>
  );
}; 